// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN — STRUCTURES (éléments d'organisation, graphe à parents multiples)
// Juin 2026. Lecture gardée par structures.read, écriture par structures.write.
// ═══════════════════════════════════════════════════════════════════════════
//
// Visualisation : arbre dépliable construit de zéro (aucun composant tree
// réutilisable n'existe). Racines = structures sans parent. Une structure à
// plusieurs parents apparaît sous chacun d'eux. Garde anti-boucle défensive
// (les cycles sont déjà refusés côté backend). Édition via popup.
//
// La logique pure du graphe (children, descendants, recalcul des parents au
// drag&drop) vit dans domain/structure-graph.ts — testée en vitest.

import type { DropStatus } from '../domain/action-plan.js';
import { dropTargetStatus } from '../domain/action-plan.js';
import {
  childrenMap as graphChildrenMap,
  computeRelinkParents,
  descendantIds as graphDescendantIds,
} from '../domain/structure-graph.js';
import { confirmDelete } from '../utils/confirm-delete.js';
import { openPilotPicker } from '../utils/pilot-picker.js';
import { clearDropHints, createTreeDndHandlers, rowFromEvent } from '../utils/tree-dnd.js';

interface StructureEntity {
  id: number;
  code: string;
  label: string;
  is_active: boolean;
  parent_ids: number[];
  responsible_ids?: number[];
  child_count?: number;
  in_use?: boolean;
}

interface StructUserMin {
  id: number;
  name?: string;
  trigram?: string | null;
  is_team?: boolean;
}

declare global {
  interface Window {
    renderStructuresTree: typeof renderStructuresTree;
    showStructureModal: typeof showStructureModal;
  }
}

let _structures: StructureEntity[] = [];
let _expanded: Set<number> = new Set();
// Pile d'annulation des changements de relations (déplacement / ajout /
// détachement). Chaque entrée = l'état des parents AVANT l'opération.
let _undoStack: Array<{ child: number; prevParents: number[] }> = [];
let _kbBound = false;

// `structures.read` garde la route, mais c'est une clé de menu : elle
// n'autorise pas l'écriture. Sans ce test, Créer / Annuler / ✎ / ✂ / ✕ et le
// glisser-déposer de l'arbre étaient rendus à tout le monde (revue front §I3).
const canWriteStructures = (): boolean =>
  !!(window.hasPermission && window.hasPermission('structures.write'));

pages.adminStructures = async function () {
  setHeader(
    t('structures.title'),
    canWriteStructures()
      ? `<span class="ml-auto" style="display:flex;gap:8px;align-items:center">
       <button class="btn btn-secondary" id="btn-undo-structure" title="${esc(t('structures.undo.title'))}">↺ ${esc(t('structures.undo.btn'))}</button>
       <button class="btn btn-primary" id="btn-new-structure">${esc(t('structures.btn.add'))}</button>
     </span>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  document.getElementById('btn-undo-structure')?.addEventListener('click', () => {
    void undoLastRelink();
  });
  // Lié ICI, une seule fois par entrée de page, et NON dans
  // `renderStructuresTree()` : ce bouton vit dans le header, que `setContent()`
  // ne remplace pas. Le câbler à chaque rendu de l'arbre empilait les
  // listeners sur le même élément — après une création, un clic déclenchait
  // deux `showStructureModal()` concurrentes. Comme la fonction est `async`
  // (elle attend users + comités), la seconde se résolvait APRÈS que
  // l'utilisateur ait commencé à saisir et rappelait `openModal`, détruisant
  // la modale en cours et effaçant la saisie sans le moindre message.
  // Le délai dépendant du volume de données, le défaut ne se voyait pas sur
  // un tenant vide (bug trouvé par l'e2e sur base seedée, 2026-07-25).
  document.getElementById('btn-new-structure')?.addEventListener('click', () => {
    void showStructureModal(null);
  });
  // Raccourci clavier Cmd/Ctrl+Z. Lié une seule fois ; no-op hors de cette page
  // (garde sur la présence de #struct-tree) → pas de fuite cross-page.
  if (!_kbBound) {
    document.addEventListener('keydown', onStructuresKeydown);
    _kbBound = true;
  }
  try {
    _structures = (await api.getStructures()) as StructureEntity[];
    _undoStack = [];
    // Premier rendu : tout déplié pour la découverte de l'organisation.
    _expanded = new Set(_structures.map((s) => s.id));
    renderStructuresTree();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function onStructuresKeydown(e: KeyboardEvent): void {
  if (!document.getElementById('struct-tree')) return; // pas sur la page
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;
  // Ne pas court-circuiter l'annulation native dans un champ de saisie/modale.
  const ae = document.activeElement as HTMLElement | null;
  if (
    ae &&
    (ae.tagName === 'INPUT' ||
      ae.tagName === 'TEXTAREA' ||
      ae.tagName === 'SELECT' ||
      ae.isContentEditable)
  ) {
    return;
  }
  e.preventDefault();
  void undoLastRelink();
}

// ── Helpers graphe ───────────────────────────────────────────────────────────

function _byId(): Map<number, StructureEntity> {
  return new Map(_structures.map((s) => [s.id, s]));
}

/** parentId -> liste d'IDs enfants, triés par code (pour le rendu de l'arbre). */
function _childrenMap(): Map<number, number[]> {
  const m = graphChildrenMap(_structures);
  const byId = _byId();
  for (const [, ids] of m) {
    ids.sort((a, b) => (byId.get(a)?.code || '').localeCompare(byId.get(b)?.code || ''));
  }
  return m;
}

/** IDs descendants de `rootId` (root exclu). Délègue au domaine. */
function _descendantIds(rootId: number): Set<number> {
  return graphDescendantIds(_structures, rootId);
}

// ── Rendu de l'arbre ─────────────────────────────────────────────────────────

function renderStructuresTree(): void {
  if (!_structures.length) {
    setContent(
      `<div class="card"><div class="empty" style="padding:40px;text-align:center;color:var(--text3)">${esc(t('structures.empty'))}</div></div>`,
    );
    _refreshUndoButton();
    return;
  }

  const byId = _byId();
  const children = _childrenMap();
  const roots = _structures
    .filter((s) => !(s.parent_ids || []).length)
    .sort((a, b) => a.code.localeCompare(b.code));

  const rootzoneHtml = `<div class="struct-rootzone" data-rootzone>${esc(t('structures.dnd.rootzone'))}</div>`;
  let html = `<div class="card">`;
  html += rootzoneHtml;
  html += `<div id="struct-tree" class="struct-tree">`;
  for (const r of roots) {
    html += buildNodeHtml(r.id, null, 0, new Set(), byId, children);
  }
  html += `</div>`;
  // 2e barre de détachement sous l'arbre (même comportement que celle du haut).
  html += rootzoneHtml;
  html += `</div>`;
  setContent(html);
  _refreshUndoButton();

  const tree = document.getElementById('struct-tree');
  if (tree) {
    // `onTreeClick` sert aussi au dépliage de l'arbre, qui est une lecture : on
    // le câble toujours. Le reste déplace des nœuds, donc écrit.
    tree.addEventListener('click', onTreeClick);
    if (canWriteStructures()) {
      tree.addEventListener('dragstart', onTreeDragStart);
      tree.addEventListener('dragover', onTreeDragOver);
      tree.addEventListener('dragleave', onTreeDragLeave);
      tree.addEventListener('drop', onTreeDrop);
      tree.addEventListener('dragend', onTreeDragEnd);
    }
  }
  if (canWriteStructures()) {
    document.querySelectorAll<HTMLElement>('[data-rootzone]').forEach((rootzone) => {
      rootzone.addEventListener('dragover', onRootZoneOver);
      rootzone.addEventListener('dragleave', onRootZoneLeave);
      rootzone.addEventListener('drop', onRootZoneDrop);
    });
  }
}

function buildNodeHtml(
  id: number,
  parentId: number | null,
  depth: number,
  path: Set<number>,
  byId: Map<number, StructureEntity>,
  children: Map<number, number[]>,
): string {
  const s = byId.get(id);
  if (!s) return '';
  // Garde anti-boucle (défensif : cycles refusés côté serveur).
  if (path.has(id)) {
    return `<div class="struct-row" style="padding-left:${12 + depth * 22}px;color:var(--warn)">↻ ${esc(s.code)} — ${esc(t('structures.cycle'))}</div>`;
  }
  const kids = children.get(id) || [];
  const hasKids = kids.length > 0;
  const isOpen = _expanded.has(id);
  const caret = hasKids
    ? `<button class="struct-caret" data-toggle="${id}" aria-label="${esc(t('structures.toggle'))}">${isOpen ? '▼' : '▶'}</button>`
    : `<span class="struct-caret struct-caret-empty"></span>`;
  const inactive = !s.is_active;
  const labelStyle = inactive ? 'color:var(--text3);font-style:italic' : 'color:var(--text)';
  const inactiveTag = inactive
    ? `<span class="struct-badge">${esc(t('structures.inactive'))}</span>`
    : '';

  const canWrite = canWriteStructures();
  const editAttr = canWrite ? `data-edit-struct="${id}"` : '';
  const editCursor = canWrite ? 'cursor:pointer' : 'cursor:default';
  let row = `
    <div class="struct-row${parentId === null ? ' struct-root' : ''}" draggable="${canWrite}" data-node="${id}" data-parent="${parentId ?? ''}" style="padding-left:${12 + depth * 22}px">
      ${canWrite ? `<span class="struct-grip" title="${esc(t('structures.dnd.hint'))}">⠿</span>` : ''}
      ${caret}
      <span class="struct-code" ${editAttr} style="${editCursor}">${esc(s.code)}</span>
      <span class="struct-label" ${editAttr} style="${labelStyle};${editCursor}">${esc(s.label)}</span>
      ${inactiveTag}
      <span class="struct-actions">${
        canWrite
          ? `<button class="btn btn-secondary btn-icon" data-edit-struct="${id}" title="${esc(t('common.edit'))}" style="color:#147A57">✎</button>
        ${
          parentId !== null
            ? `<button class="btn btn-secondary btn-icon" data-detach-struct="${id}" data-detach-parent="${parentId}" title="${esc(t('structures.detach.title'))}">✂</button>`
            : ''
        }
        <button class="btn btn-danger btn-icon" data-del-struct="${id}" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }</span>
    </div>`;

  if (hasKids && isOpen) {
    const childPath = new Set(path);
    childPath.add(id);
    for (const cid of kids) {
      row += buildNodeHtml(cid, id, depth + 1, childPath, byId, children);
    }
  }
  return row;
}

function onTreeClick(e: Event): void {
  const target = (e.target as HTMLElement | null)?.closest(
    '[data-toggle],[data-edit-struct],[data-detach-struct],[data-del-struct]',
  );
  if (!(target instanceof HTMLElement)) return;
  const toggle = target.dataset['toggle'];
  if (toggle !== undefined) {
    const id = parseInt(toggle, 10);
    if (_expanded.has(id)) _expanded.delete(id);
    else _expanded.add(id);
    renderStructuresTree();
    return;
  }
  const editId = target.dataset['editStruct'];
  if (editId !== undefined) {
    const s = _structures.find((x) => x.id === parseInt(editId, 10));
    if (s) void showStructureModal(s);
    return;
  }
  const detachId = target.dataset['detachStruct'];
  if (detachId !== undefined) {
    const parent = parseInt(target.dataset['detachParent'] || '', 10);
    if (!Number.isNaN(parent)) void applyRelink(parseInt(detachId, 10), parent, null, {});
    return;
  }
  const delId = target.dataset['delStruct'];
  if (delId !== undefined) {
    const s = _structures.find((x) => x.id === parseInt(delId, 10));
    if (s) confirmDeleteStructure(s);
  }
}

// ── Drag & drop : déplacement de liens dans le graphe ────────────────────────
// Chaque nœud affiché = une arête (parent → enfant). Glisser le nœud déplace
// CETTE arête : l'enfant quitte le parent sous lequel il était affiché et passe
// sous la cible (ses autres parents restent). Déposer sur la zone « racine »
// détache (retire ce parent). Cycles refusés (cible ≠ descendant). Le backend
// revérifie de toute façon.

let _drag: { child: number; fromParent: number | null } | null = null;

/** Statut d'une cible de dépôt pour la structure en cours de glissé.

    Délègue à `domain/structure-graph.dropTargetStatus` — la même règle était
    réimplémentée ici à la main (soi-même / cycle / noop) alors que
    `page-action-plans.ts` la consommait déjà depuis `domain/`, testée
    (audit front du 2026-09-10). */
function _targetStatus(targetId: number): DropStatus {
  return dropTargetStatus(_drag, targetId, _drag ? _descendantIds(_drag.child) : new Set<number>());
}

// Les trois gestionnaires viennent de `utils/tree-dnd.ts` : ils étaient
// dupliqués avec `page-action-plans.ts`, dont `onTreeDragStart` byte pour byte.
const {
  onDragStart: onTreeDragStart,
  onDragOver: onTreeDragOver,
  onDrop: onTreeDrop,
} = createTreeDndHandlers({
  getDrag: () => _drag,
  setDrag: (d) => {
    _drag = d;
  },
  descendantIds: _descendantIds,
  targetStatus: _targetStatus,
  applyRelink: (child, fromParent, target, add) => {
    void applyRelink(child, fromParent, target, { add });
  },
});

function onTreeDragLeave(e: DragEvent): void {
  rowFromEvent(e)?.classList.remove('struct-drop-target', 'struct-drop-invalid');
}

function onTreeDragEnd(): void {
  _drag = null;
  clearDropHints();
  document
    .querySelectorAll('.struct-dragging')
    .forEach((el) => el.classList.remove('struct-dragging'));
}

function onRootZoneOver(e: DragEvent): void {
  if (!_drag || _drag.fromParent === null) return; // déjà racine → rien à détacher
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  (e.currentTarget as HTMLElement).classList.add('dnd-over');
}

function onRootZoneLeave(e: DragEvent): void {
  (e.currentTarget as HTMLElement).classList.remove('dnd-over');
}

function onRootZoneDrop(e: DragEvent): void {
  if (!_drag || _drag.fromParent === null) return;
  e.preventDefault();
  const drag = _drag;
  void applyRelink(drag.child, drag.fromParent, null, {});
}

async function applyRelink(
  child: number,
  fromParent: number | null,
  targetParent: number | null,
  opts: { add?: boolean },
): Promise<void> {
  const add = !!opts.add;
  const s = _structures.find((x) => x.id === child);
  if (!s) return;
  // Recalcul pur (déplacement / ajout Maj / détachement) — cf. domain.
  const parents = computeRelinkParents(s.parent_ids || [], fromParent, targetParent, add);
  const toastKey =
    targetParent === null
      ? 'structures.toast.detached'
      : add
        ? 'structures.toast.linked'
        : 'structures.toast.relinked';
  await _persistParents(child, parents, toastKey, true);
}

/** Persiste un nouveau jeu de parents pour `child`. `pushUndo` empile l'état
 *  précédent dans la pile d'annulation (faux pour l'annulation elle-même). */
async function _persistParents(
  child: number,
  parents: number[],
  toastKey: string,
  pushUndo: boolean,
): Promise<void> {
  const s = _structures.find((x) => x.id === child);
  if (!s) return;
  const before = (s.parent_ids || []).slice();
  // Aucun changement effectif → on ne fait rien.
  if (before.length === parents.length && parents.every((p) => before.includes(p))) return;
  try {
    const saved = (await api.updateStructure(child, {
      code: s.code,
      label: s.label,
      is_active: s.is_active,
      parent_ids: parents,
    })) as StructureEntity;
    const idx = _structures.findIndex((x) => x.id === child);
    if (idx !== -1) _structures[idx] = saved;
    if (pushUndo) _undoStack.push({ child, prevParents: before });
    toast(t(toastKey));
    renderStructuresTree();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('common.error');
    toast(msg, 'error');
  }
}

async function undoLastRelink(): Promise<void> {
  const entry = _undoStack.pop();
  if (!entry) {
    toast(t('structures.undo.empty'), 'info');
    return;
  }
  await _persistParents(entry.child, entry.prevParents, 'structures.undo.done', false);
}

function _refreshUndoButton(): void {
  const btn = document.getElementById('btn-undo-structure') as HTMLButtonElement | null;
  if (btn) btn.disabled = _undoStack.length === 0;
}

// ── Suppression ──────────────────────────────────────────────────────────────

function confirmDeleteStructure(s: StructureEntity): void {
  confirmDelete({
    titleKey: 'structures.delete.title',
    bodyKey: 'structures.delete.body',
    bodyParams: { label: `${s.code} — ${s.label}` },
    onDelete: () => api.deleteStructure(s.id),
    onDeleted: () => {
      toast(t('structures.toast.deleted'));
      _structures = _structures.filter((x) => x.id !== s.id);
      renderStructuresTree();
    },
  });
}

// ── Modale création / édition ────────────────────────────────────────────────

async function showStructureModal(structure: StructureEntity | null): Promise<void> {
  const isNew = !structure;
  const s: StructureEntity = structure || {
    id: 0,
    code: '',
    label: '',
    is_active: true,
    parent_ids: [],
  };

  // Parents sélectionnables : toutes les structures sauf elle-même et ses
  // descendants (anti-cycle ; le backend revérifie de toute façon).
  const excluded = isNew ? new Set<number>() : _descendantIds(s.id);
  if (!isNew) excluded.add(s.id);
  const selectable = _structures
    .filter((x) => !excluded.has(x.id))
    .sort((a, b) => a.code.localeCompare(b.code));
  const currentParents = new Set(s.parent_ids || []);

  // Utilisateurs (pour le picker « responsables », copié des pilotes programme).
  let users: StructUserMin[] = [];
  try {
    users = (await api.getUsers()) as StructUserMin[];
  } catch {
    /* on continue sans le bloc responsables */
  }
  const indivUsers = (users || []).filter((u) => !u.is_team);
  const selectedResp = new Set<number>(s.responsible_ids || []);
  const renderRespChips = (): string => {
    const chips = Array.from(selectedResp)
      .map((id) => indivUsers.find((u) => u.id === id))
      .filter((u): u is StructUserMin => Boolean(u))
      .map(
        (u) => `<span class="st-resp-chip" data-uid="${u.id}"
        style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);
               font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">
        ${esc(u.trigram || u.name || '')}
        <button type="button" class="st-resp-remove" data-uid="${u.id}"
          style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
      </span>`,
      )
      .join('');
    return (
      chips ||
      `<span style="color:var(--text3);font-size:11px">${esc(t('structures.responsibles.none'))}</span>`
    );
  };

  const parentsHtml = selectable.length
    ? selectable
        .map(
          (x) => `
        <label class="struct-parent-opt">
          <input type="checkbox" class="st-parent-chk" value="${x.id}" ${currentParents.has(x.id) ? 'checked' : ''}>
          <span><strong>${esc(x.code)}</strong> — ${esc(x.label)}${x.is_active ? '' : ` <em style="color:var(--text3)">(${esc(t('structures.inactive'))})</em>`}</span>
        </label>`,
        )
        .join('')
    : `<p style="font-size:12px;color:var(--text3);margin:0">${esc(t('structures.modal.noParent'))}</p>`;

  const body = `
    <div class="form-row">
      <div class="form-group" style="flex:1"><label>${esc(t('structures.field.code'))}</label>
        <input type="text" id="st-code" maxlength="8" value="${esc(s.code)}"
          placeholder="${esc(t('structures.field.code.placeholder'))}" style="text-transform:uppercase;font-family:var(--font-mono);width:100%"></div>
      <div class="form-group" style="flex:3"><label>${esc(t('structures.field.label'))}</label>
        <input type="text" id="st-label" value="${esc(s.label)}" placeholder="${esc(t('structures.field.label.placeholder'))}" style="width:100%"></div>
    </div>
    <div class="form-group" style="margin-top:8px">
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;font-weight:400;margin:0">
        <input type="checkbox" id="st-active" ${s.is_active ? 'checked' : ''}>
        <span>${esc(t('structures.field.active'))}</span>
      </label>
    </div>
    <div class="form-group" style="margin-top:10px">
      <label>${esc(t('structures.field.responsibles'))}</label>
      <p style="font-size:11px;color:var(--text3);margin:0 0 6px">${esc(t('structures.field.responsibles.help'))}</p>
      <div id="st-resp-block"
        style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;display:flex;align-items:flex-start;gap:6px;cursor:pointer">
        <div id="st-resp-chips" style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px;align-items:center">${renderRespChips()}</div>
        <button type="button" id="st-resp-add-btn" class="btn btn-secondary btn-icon"
          style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start" title="${esc(t('structures.responsibles.addTitle'))}">+</button>
      </div>
    </div>
    <div class="form-group" style="margin-top:10px">
      <label>${esc(t('structures.field.parents'))}</label>
      <p style="font-size:11px;color:var(--text3);margin:0 0 6px">${esc(t('structures.field.parents.help'))}</p>
      <div class="struct-parents-box">${parentsHtml}</div>
    </div>`;

  openModal(
    isNew ? t('structures.modal.create.title') : t('structures.modal.update.title'),
    body,
    async () => {
      const codeEl = document.getElementById('st-code') as HTMLInputElement | null;
      const labelEl = document.getElementById('st-label') as HTMLInputElement | null;
      const activeEl = document.getElementById('st-active') as HTMLInputElement | null;
      const code = codeEl?.value.trim().toUpperCase() || '';
      const label = labelEl?.value.trim() || '';
      if (!code || !label) {
        toast(t('structures.error.code_label_required'), 'error');
        return;
      }
      const parent_ids = Array.from(
        document.querySelectorAll<HTMLInputElement>('.st-parent-chk:checked'),
      ).map((c) => parseInt(c.value, 10));
      const responsible_ids = Array.from(selectedResp);
      const payload = { code, label, is_active: !!activeEl?.checked, parent_ids, responsible_ids };
      try {
        const saved = (
          isNew
            ? await api.createStructure(payload)
            : structure
              ? await api.updateStructure(structure.id, payload)
              : null
        ) as StructureEntity | null;
        if (!saved) return;
        closeModal();
        toast(isNew ? t('structures.toast.created') : t('structures.toast.updated'));
        const idx = _structures.findIndex((x) => x.id === saved.id);
        if (idx === -1) {
          _structures.push(saved);
          _expanded.add(saved.id);
        } else {
          _structures[idx] = saved;
        }
        renderStructuresTree();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );

  setTimeout(() => {
    document.getElementById('st-code')?.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (target) target.value = target.value.toUpperCase();
    });
    _wireResponsiblesPicker(indivUsers, selectedResp, renderRespChips);
  }, 0);
}

/** Picker « responsables » : chips + bouton +, puis le dropdown partagé
 *  `utils/pilot-picker`. C'était une copie fidèle du picker des pilotes de
 *  programme jusqu'à l'audit front du 2026-09-10 ; seul le câblage des chips
 *  reste ici, le dropdown est le même code pour les cinq points d'appel. */
function _wireResponsiblesPicker(
  indivUsers: StructUserMin[],
  selectedResp: Set<number>,
  renderRespChips: () => string,
): void {
  const refreshChips = (): void => {
    const el = document.getElementById('st-resp-chips');
    if (el) el.innerHTML = renderRespChips();
    document.querySelectorAll<HTMLElement>('.st-resp-remove').forEach((b) => {
      b.addEventListener('click', (ev: Event) => {
        ev.stopPropagation();
        const uid = parseInt(b.getAttribute('data-uid') || '', 10);
        if (!Number.isNaN(uid)) selectedResp.delete(uid);
        refreshChips();
      });
    });
  };
  refreshChips();

  // Clic sur le champ (zone chips) = clic sur « + » : ouvre le picker. On exclut
  // les boutons « × » de retrait et le bouton « + » lui-même (géré à part).
  document.getElementById('st-resp-block')?.addEventListener('click', (ev: Event) => {
    const tgt = ev.target as HTMLElement | null;
    if (!tgt || tgt.closest('.st-resp-remove') || tgt.closest('#st-resp-add-btn')) return;
    (document.getElementById('st-resp-add-btn') as HTMLButtonElement | null)?.click();
  });

  document.getElementById('st-resp-add-btn')?.addEventListener('click', (ev: Event) => {
    ev.stopPropagation();
    openPilotPicker({
      anchor: ev.currentTarget as HTMLElement,
      users: indivUsers,
      selected: selectedResp,
      prefix: 'st-resp',
      labels: {
        search: t('structures.responsibles.search'),
        empty: t('structures.responsibles.noResult'),
        add: t('structures.responsibles.add'),
      },
      onClose: refreshChips,
    });
  });
}

window.renderStructuresTree = renderStructuresTree;
window.showStructureModal = showStructureModal;

export {};
